import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarEvent, MealDetailsByDate, MealSignalData, Signal } from '@demind-inc/core';
import dayjs from 'dayjs';

import { metricsApi } from '../api';
import { useRecoilState } from 'recoil';
import { eventsSnackBarAtom } from '../recoil';

interface UseUpdateMealDetailsParams {
  userId: string;
  metricId: string;
  detailsByDate: MealDetailsByDate[];
}

export function useUpdateMealDetails() {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const updateMealDetailMutation = useMutation({
    mutationFn: ({ userId, metricId, detailsByDate }: UseUpdateMealDetailsParams) => {
      return metricsApi.updateMeal(metricId, userId, {
        mealDetails: detailsByDate,
      });
    },
    onMutate: async ({ detailsByDate }) => {
      const hasUpdate = detailsByDate.some((item) => item.details);
      if (!hasUpdate) return {};
      setEventsSnackbar({ message: 'Event updating...', type: 'loading' });

      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.events'] });

      const queries = queryClient.getQueriesData({ queryKey: ['lifestack.calendar.events'] });
      const previousQueries: [QueryKey, CalendarEvent[]][] = [];

      queries.forEach(([queryKey, data]) => {
        if (data && Array.isArray(data)) {
          previousQueries.push([queryKey, data]);

          let updatedData = data;

          detailsByDate.forEach((item) => {
            if (!item.details || item.details.length === 0) return;

            const date = item.date;

            item.details.forEach((detail) => {
              const mealType = detail.type;
              if (!mealType) return;

              updatedData = updatedData.map((event: CalendarEvent) => {
                const isMealEvent = event.autoScheduleFrom?.from === 'meal';
                if (!isMealEvent) return event;

                if (!dayjs(event.start?.date).isSame(date, 'day')) return event;

                const eventName = event.summary?.toLowerCase();
                if (!eventName?.includes(mealType)) return event;

                return {
                  ...event,
                  start: {
                    ...event.start,
                    date: detail.startTime,
                  },
                  end: {
                    ...event.end,
                    date: detail.endTime,
                  },
                  richContext: {
                    ...event.richContext,
                    signals: [
                      ...(event.richContext?.signals || []).filter(
                        (signal) => signal.signal_source !== 'meal'
                      ),
                      {
                        signal_source: 'meal',
                        body: detail.type,
                        data: {
                          type: detail.type,
                          title: detail.title,
                          food_items: detail.foodItems,
                          macros: detail.macros,
                          micros: detail.micros,
                          start_time: detail.startTime,
                          end_time: detail.endTime,
                        } as MealSignalData,
                      } as Signal,
                    ],
                  },
                };
              });
            });
          });

          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { previousQueries };
    },
    onSuccess: (_, { detailsByDate }) => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.meal.details'] });
      const hasUpdate = detailsByDate.some((item) => item.details);
      if (hasUpdate) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
        setEventsSnackbar({ message: 'Event updated', type: 'success' });
      }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });

  return {
    updateMealDetail: updateMealDetailMutation.mutateAsync,
    ...updateMealDetailMutation,
  };
}
